/**
 * Estado de la ayuda en pantalla: qué tema está abierto y cuál es el de la pantalla actual. El botón Ayuda (o F1) abre
 * el de la pestaña o ventana que la pantalla haya anunciado con `useTemaAyuda`, o si no el de la ruta.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { PanelAyuda } from './PanelAyuda.tsx';
import { temaDeRuta } from './registro.ts';

interface EstadoAyuda {
  /** Tema abierto en el panel (null: cerrado). */
  abierto: string | null;
  /** Tema de la pantalla: el que anunció la pestaña activa, o el de la ruta. */
  temaPantalla: string | null;
  abrir: (id?: string) => void;
  cerrar: () => void;
  fijarSubtema: (id: string | null) => void;
}

const Contexto = createContext<EstadoAyuda | null>(null);
/** Tema general cuando la ruta no tiene ayuda propia. */
export const TEMA_GENERAL = 'general';

export function AyudaProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [subtema, setSubtema] = useState<{ ruta: string; id: string } | null>(null);
  const temaRuta = temaDeRuta(pathname)?.id ?? null;
  // Un subtema anunciado en otra pantalla no vale aquí
  const temaPantalla = subtema && subtema.ruta === pathname ? subtema.id : temaRuta;
  const abrir = useCallback((id?: string) => setAbierto(id ?? temaPantalla ?? TEMA_GENERAL), [temaPantalla]);
  const cerrar = useCallback(() => setAbierto(null), []);
  const fijarSubtema = useCallback((id: string | null) => setSubtema(id ? { ruta: pathname, id } : null), [pathname]);

  // F1 abre la ayuda de la pantalla (y no la del navegador)
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key !== 'F1') return;
      e.preventDefault();
      setAbierto((a) => (a ? null : (temaPantalla ?? TEMA_GENERAL)));
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [temaPantalla]);

  const valor = useMemo(() => ({ abierto, temaPantalla, abrir, cerrar, fijarSubtema }), [abierto, temaPantalla, abrir, cerrar, fijarSubtema]);
  return (
    <Contexto.Provider value={valor}>
      {children}
      <PanelAyuda />
    </Contexto.Provider>
  );
}

/** Para los botones de ayuda: sin el proveedor (un componente probado suelto) no hay ayuda que abrir. */
export const useAyudaOpcional = (): EstadoAyuda | null => useContext(Contexto);

export function useAyuda(): EstadoAyuda {
  const c = useContext(Contexto);
  if (!c) throw new Error('useAyuda va dentro de AyudaProvider');
  return c;
}

/**
 * La pantalla anuncia la ayuda de lo que se ve ahora (la pestaña activa): el botón Ayuda abre ese tema. Al salir, se
 * vuelve al de la ruta.
 */
export function useTemaAyuda(id: string | null) {
  const c = useContext(Contexto);
  const fijar = c?.fijarSubtema;
  useEffect(() => {
    if (!fijar) return;
    fijar(id);
    return () => fijar(null);
  }, [fijar, id]);
}
